import { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Globe, Settings, Edit3, Layout, Gift, Save, Loader2, Plus, Trash2, Eye } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import AdminSiteSettingsPage from './AdminSiteSettingsPage';
import AdminPageEditorPage from './AdminPageEditorPage';
import AdminPageExtrasPage from './AdminPageExtrasPage';

function HomepagePopupEditor() {
  const { api } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/site-settings/homepage-popup/').then(res => {
      setConfig(res.data);
    }).catch(() => {
      setConfig({
        enabled: true,
        title: 'Special Enrollment Giveaway',
        subtitle: 'Limited Time Offer!',
        description: 'Enroll in a course and get 100 bonus points to kickstart your rewards journey!',
        bullets: ['100 Welcome Points', '50 Points per Referral', 'Exclusive Early Access'],
        button_text: 'Enroll Now & Get Bonus Points',
        button_link: '/register'
      });
    }).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/site-settings/homepage-popup/', config);
      toast.success('Homepage popup updated! Changes are live.');
    } catch (err) {
      toast.error('Failed to save popup settings');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const updateBullet = (index, value) => {
    const bullets = [...(config.bullets || [])];
    bullets[index] = value;
    setConfig(prev => ({ ...prev, bullets }));
  };

  const addBullet = () => {
    setConfig(prev => ({ ...prev, bullets: [...(prev.bullets || []), ''] }));
  };

  const removeBullet = (index) => {
    const bullets = [...(config.bullets || [])];
    bullets.splice(index, 1);
    setConfig(prev => ({ ...prev, bullets }));
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      {/* Enable/Disable Toggle */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="w-5 h-5" />
            Homepage Popup
          </CardTitle>
          <CardDescription>Configure the promotional popup that appears on the homepage hero section.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 rounded-lg border">
            <div>
              <p className="font-semibold">Show Popup on Homepage</p>
              <p className="text-sm text-muted-foreground">When disabled, the popup will not appear for any visitors.</p>
            </div>
            <Switch
              checked={config?.enabled ?? true}
              onCheckedChange={(checked) => updateField('enabled', checked)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Content Editor */}
      <Card>
        <CardHeader>
          <CardTitle>Popup Content</CardTitle>
          <CardDescription>Edit the text that appears in the popup banner.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="popup-title">Title</Label>
              <Input
                id="popup-title"
                value={config?.title || ''}
                onChange={(e) => updateField('title', e.target.value)}
                placeholder="e.g., Special Enrollment Giveaway"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="popup-subtitle">Subtitle</Label>
              <Input
                id="popup-subtitle"
                value={config?.subtitle || ''}
                onChange={(e) => updateField('subtitle', e.target.value)}
                placeholder="e.g., Limited Time Offer!"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="popup-description">Description</Label>
            <textarea
              id="popup-description"
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              value={config?.description || ''}
              onChange={(e) => updateField('description', e.target.value)}
              placeholder="Describe the offer..."
            />
          </div>
        </CardContent>
      </Card>

      {/* Bullet Points */}
      <Card>
        <CardHeader>
          <CardTitle>Bullet Points</CardTitle>
          <CardDescription>Add highlight items that appear with checkmarks.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {(config?.bullets || []).map((bullet, index) => (
            <div key={index} className="flex items-center gap-2">
              <span className="text-green-600 font-bold">✓</span>
              <Input
                value={bullet}
                onChange={(e) => updateBullet(index, e.target.value)}
                placeholder={`Bullet point ${index + 1}`}
                className="flex-1"
              />
              <Button variant="ghost" size="icon" onClick={() => removeBullet(index)} className="text-red-500 hover:text-red-700 hover:bg-red-50">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addBullet} className="mt-2">
            <Plus className="w-4 h-4 mr-1" /> Add Bullet Point
          </Button>
        </CardContent>
      </Card>

      {/* Button Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Call-to-Action Button</CardTitle>
          <CardDescription>Configure the button at the bottom of the popup.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="popup-btn-text">Button Text</Label>
              <Input
                id="popup-btn-text"
                value={config?.button_text || ''}
                onChange={(e) => updateField('button_text', e.target.value)}
                placeholder="e.g., Enroll Now & Get Bonus Points"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="popup-btn-link">Button Link</Label>
              <Input
                id="popup-btn-link"
                value={config?.button_link || ''}
                onChange={(e) => updateField('button_link', e.target.value)}
                placeholder="e.g., /register"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Eye className="w-5 h-5" /> Preview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-yellow-400 rounded-2xl p-6 shadow-lg relative max-w-md">
            <div className="flex items-start gap-3 mb-4">
              <div className="bg-blue-900 rounded-full p-2">
                <Gift className="w-6 h-6 text-yellow-400" />
              </div>
              <div>
                <h3 className="font-bold text-xl text-blue-900">{config?.title || 'Title'}</h3>
                <p className="text-blue-900/80 text-sm">{config?.subtitle || 'Subtitle'}</p>
              </div>
            </div>
            <p className="text-blue-900/90 mb-4">{config?.description || 'Description...'}</p>
            <ul className="space-y-2 mb-4">
              {(config?.bullets || []).map((b, i) => (
                <li key={i} className="flex items-center gap-2 text-blue-900">
                  <span className="text-green-600">✓</span> {b}
                </li>
              ))}
            </ul>
            <div className="w-full bg-blue-900 text-white text-center py-2 rounded-md font-bold">
              {config?.button_text || 'Button Text'}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} className="px-8">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Popup Settings
        </Button>
      </div>
    </div>
  );
}

export default function AdminWebsiteSettingsPage() {
  const frontendUrl = window.location.origin;
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
            <Globe className="w-6 h-6" /> Content Management
          </h1>
          <p className="text-muted-foreground">Manage navigation, banners, popups, and content sections</p>
        </div>
        <Button variant="outline" onClick={() => window.open(frontendUrl, '_blank')}>
          <Eye className="h-4 w-4 mr-2" /> Preview Site
        </Button>
      </div>

      <Tabs defaultValue="navigation" className="w-full">
        <TabsList>
          <TabsTrigger value="navigation" className="text-sm">
            <Settings className="w-4 h-4 mr-2" /> Navigation
          </TabsTrigger>
          <TabsTrigger value="page-extras" className="text-sm">
            <Layout className="w-4 h-4 mr-2" /> Page Extras
          </TabsTrigger>
        </TabsList>

        <TabsContent value="navigation" className="mt-6">
          <AdminSiteSettingsPage />
        </TabsContent>

        <TabsContent value="page-extras" className="mt-6">
          <AdminPageExtrasPage />
        </TabsContent>
      </Tabs>
    </div>
  );
}
